import { Demand } from '../../../types/Demand';
import { DemandProduct } from '../../../types/DemandProduct';
import { PurchaseRequisitionResult } from '../../../../purchase-requisition/types/PurchaseRequisitionResult';

export class ValidationValue {
    private _touched: boolean;

    get touched(): boolean {
        return this._touched;
    }

    get value(): boolean {
        return this._value;
    }

    set value(newValue: boolean) {
        const oldValue = this.value;
        this._value = newValue;
        if (oldValue !== newValue) {
            this._touched = true;
        }
    }

    public report(newValue: boolean): void {
        this._value = newValue;
        this._touched = true;
    }

    get showError(): boolean {
        return !this._value && this._touched;
    }

    constructor(private _value: boolean) {
        this._touched = false;
    }
}

export class DemandValidator {
    private readonly _accountingValid = new Map<string, ValidationValue>();
    private readonly _classificationValid = new Map<string, ValidationValue>();
    private readonly _supplierArticleNumberValid = new Map<string, ValidationValue>();
    private readonly _deliveryDateValid = new Map<string, ValidationValue>();
    private readonly _articlePriceValidForOrder = new Map<string, ValidationValue>();
    private readonly _siteValid = new Map<string, ValidationValue>();

    private readonly _suppliersValid: ValidationValue;
    private readonly _validUntilValid: ValidationValue;
    private readonly _currencyValid: ValidationValue;
    // EPROC-11560, order-scoped since the EPROC-11425/11560 Konsolidierung: verdict whether the
    // supplier-currency rule blocks the next step. The component feeds it with
    // `releaseAllowed || !resultsInOrder`, so it only ever blocks a flow that ends in an ORDER.
    // Starts invalid on purpose: an order release must stay blocked until the asynchronous check
    // answered, so an incompatible demand can never be released before the verdict is known.
    private readonly _supplierCurrencyValid = new ValidationValue(false);

    get accountingValid(): boolean {
        return Array.from(this._accountingValid.values()).every((isValid) => isValid.value);
    }

    get showAccountingError(): boolean {
        return this.validationComplete(this._accountingValid) && !this.accountingValid;
    }

    get classificationValid(): boolean {
        return Array.from(this._classificationValid.values()).every((isValid) => isValid.value);
    }

    get showClassificationError(): boolean {
        return this.validationComplete(this._classificationValid) && !this.classificationValid;
    }

    // EPROC-10907: every visible site selector reports its validity (required + empty => invalid).
    get siteValid(): boolean {
        return Array.from(this._siteValid.values()).every((isValid) => isValid.value);
    }

    get showSiteError(): boolean {
        return this.validationComplete(this._siteValid) && !this.siteValid;
    }

    get supplierArticleNumberValid(): boolean {
        return Array.from(this._supplierArticleNumberValid.values()).every((isValid) => isValid.value);
    }

    get deliveryDateValid(): boolean {
        return Array.from(this._deliveryDateValid.values()).every((isValid) => isValid.value);
    }

    get articlePriceValidForOrder(): boolean {
        return Array.from(this._articlePriceValidForOrder.values()).every((isValid) => isValid.value);
    }

    get suppliersValid(): ValidationValue {
        return this._suppliersValid;
    }

    get validUntilValid(): ValidationValue {
        return this._validUntilValid;
    }

    get currencyValid(): ValidationValue {
        return this._currencyValid;
    }

    get supplierCurrencyValid(): ValidationValue {
        return this._supplierCurrencyValid;
    }

    /**
     * EPROC-11560: takes over the server-side verdict whether the supplier accepts the document
     * currency of the demand.
     */
    public setSupplierCurrencyValidation(validationState: boolean): void {
        this._supplierCurrencyValid.value = validationState;
    }

    public validForNextStep(): boolean {
        return (
            this.accountingValid &&
            this.classificationValid &&
            this.siteValid &&
            this.validUntilValid.value &&
            this.suppliersValid.value &&
            this.supplierArticleNumberValid &&
            this.deliveryDateValid &&
            this.purchasingGroupValid &&
            // reactivate this when we have a dedicated setting for it
            // this.articlePriceValidForOrder &&
            this._demand.valid &&
            this.currencyValid.value &&
            this.supplierCurrencyValid.value &&
            !!this._demand.companyCodeId
        );
    }

    get purchasingGroupValid(): boolean {
        if (!this.options.purchasingGroupRequired) {
            return true;
        }
        return this._demand.products.every((product) => !!product.purchasingGroup);
    }

    get validForRfq(): boolean {
        const hasSupplierArticleNumber = this._demand.products.some((p) => !!p.data.supplierArticleNumber);
        const supplierArticleNumberValidForRfq = !hasSupplierArticleNumber || this._demand.suppliers.length === 1;
        return (
            this.accountingValid &&
            this.classificationValid &&
            this.siteValid &&
            this.validUntilValidForRfq &&
            this._demand.suppliers.length > 0 &&
            supplierArticleNumberValidForRfq &&
            // EPROC-11425: absichtlich das ValidationValue-Objekt und nicht `.value` - dieser
            // Vergleich ist damit immer truthy. Nicht die Vorlage fuer die Zeile darueber:
            // `validUntilValidForRfq` liest bewusst den Wert. Der Fix gehoert zur
            // Waehrungspruefung (EPROC-11425), weil er zusaetzlich Bedarfe mit gemischten
            // Waehrungen blocken wuerde; `validForNextStep` liest oben korrekt `.value`.
            this.currencyValid &&
            // EPROC-11425/11560 Konsolidierung: harter Block nur Bestellweg — the RFQ path is
            // deliberately NOT gated by the supplier-currency verdict (decision 06.08.2026).
            this._demand.valid &&
            !!this._demand.companyCodeId
        );
    }

    constructor(
        private readonly _demand: Demand,
        public readonly options: DemandValidatorOptions,
    ) {
        this._suppliersValid = new ValidationValue(this.getSupplierValidationState());
        this._validUntilValid = new ValidationValue(this.getValidUntilValidationState());
        this._currencyValid = new ValidationValue(this.getCurrencyValidationState());

        for (const product of _demand.products) {
            this._supplierArticleNumberValid.set(
                product.itemId!,
                new ValidationValue(this.getSupplierArticleNumberValidationState(product)),
            );
            this.seedPendingAsInvalid(product.itemId!);
        }
    }

    /**
     * EPROC-11619: `demand`/`purchaseRequisition` can legitimately be reassigned to a new object
     * instance for the very same demand while a page stays open (e.g. a task-hosting parent
     * reassigning it after "Aufgabe mir zuweisen" flips `readOnly`) - the component must always pick
     * up that fresh instance (a `DemandValidator` pins its `_demand` reference for its whole lifetime,
     * so simply reusing the old validator would keep serving stale `readOnly`/`valid`/etc. state).
     * But a plain `new DemandValidator(...)` for the fresh instance would just as fully discard every
     * per-item validity report already collected - accounting-selection-v2 etc. get nudged to
     * re-validate again later anyway, but lib-site-selection normally never gets new inputs after
     * mounting, so it only ever gets one chance to report and would be stuck on the freshly seeded
     * "invalid" default forever. This builds the fresh, correctly-wired validator and then carries
     * over whatever the previous one had already genuinely heard back on a per-item basis.
     */
    public static createPreservingItemReports(
        previous: DemandValidator | undefined,
        demand: Demand,
        options: DemandValidatorOptions,
    ): DemandValidator {
        const validator = new DemandValidator(demand, options);
        if (previous) {
            validator.adoptItemReportsFrom(previous);
        }
        return validator;
    }

    private adoptItemReportsFrom(previous: DemandValidator): void {
        const currentItemIds = new Set(this._demand.products.map((product) => product.itemId!));
        DemandValidator.adoptTouchedReports(this._accountingValid, previous._accountingValid, currentItemIds);
        DemandValidator.adoptTouchedReports(this._classificationValid, previous._classificationValid, currentItemIds);
        DemandValidator.adoptTouchedReports(this._siteValid, previous._siteValid, currentItemIds);
    }

    private static adoptTouchedReports(
        target: Map<string, ValidationValue>,
        source: ReadonlyMap<string, ValidationValue>,
        currentItemIds: ReadonlySet<string>,
    ): void {
        for (const [itemId, value] of source) {
            // Only a genuine report, never the seeded "not reported yet" default - and only for a
            // position that still exists, so a removed one does not leave a phantom entry behind.
            if (value.touched && currentItemIds.has(itemId)) {
                target.set(itemId, value);
            }
        }
    }

    /**
     * EPROC-11619: accountingValid/classificationValid/siteValid are `every()` over these per-item
     * maps, which is vacuously true while a position's map entry is simply missing. Entries are only
     * set once that position's accounting-selection-v2/classification-selection/lib-site-selection
     * actually mounts and reports - and that mount is now deferred (`@defer` on viewport). Without
     * this, a position not yet mounted would silently count as valid instead of blocking submission,
     * letting "Bedarf melden"/"Anfrage an Lieferanten senden" go through before most positions have
     * been validated at all. Seed every known position as invalid up front; a real report (valid or
     * not) always overwrites this via set*ItemValidation, never the other way round.
     */
    private seedPendingAsInvalid(itemId: string): void {
        if (!this._accountingValid.has(itemId)) {
            this._accountingValid.set(itemId, new ValidationValue(false));
        }
        if (!this._classificationValid.has(itemId)) {
            this._classificationValid.set(itemId, new ValidationValue(false));
        }
        if (!this._siteValid.has(itemId)) {
            this._siteValid.set(itemId, new ValidationValue(false));
        }
    }

    public update(): void {
        this._validUntilValid.value = this.getValidUntilValidationState();
        this._suppliersValid.value = this.getSupplierValidationState();
        this._currencyValid.value = this.getCurrencyValidationState();

        for (const product of this._demand.products) {
            // EPROC-11619: seeds accounting/classification/site as invalid for a position that has no
            // entry yet (e.g. one added after construction, via addProduct()) - see seedPendingAsInvalid.
            this.seedPendingAsInvalid(product.itemId!);
            this.setSupplierArticleNumberValidation(
                product.itemId!,
                this.getSupplierArticleNumberValidationState(product),
            );
            this.setDeliveryDateValidation(
                product.itemId!,
                !this.options.deliveryDateRequired ||
                    !!product.delivery.preferredDeliveryDate ||
                    !!this._demand.readOnly,
            );
            // reactivate this when we have a dedicated setting for it
            // this.setArticlePriceValidForOrder(product.itemId!, this.getArticlePriceValidForOrder(product, resultsIn));
        }
    }

    public setAccountingItemValidation(itemId: string, validationState: boolean): void {
        if (!this._accountingValid.has(itemId)) {
            this._accountingValid.set(itemId, new ValidationValue(false));
        }
        this._accountingValid.get(itemId)!.report(validationState);
    }

    public removeItemValidation(itemId: string): void {
        this._accountingValid.delete(itemId);
        this._classificationValid.delete(itemId);
        this._supplierArticleNumberValid.delete(itemId);
        this._siteValid.delete(itemId);
    }

    public areAccountingValid(): boolean {
        return Array.from(this._accountingValid.values()).every((isValid) => isValid);
    }

    public setClassificationItemValidation(itemId: string, validationState: boolean): void {
        if (!this._classificationValid.has(itemId)) {
            this._classificationValid.set(itemId, new ValidationValue(false));
        }
        this._classificationValid.get(itemId)!.report(validationState);
    }

    public setSiteItemValidation(itemId: string, validationState: boolean): void {
        if (!this._siteValid.has(itemId)) {
            this._siteValid.set(itemId, new ValidationValue(false));
        }
        this._siteValid.get(itemId)!.report(validationState);
    }

    public areClassificationsValid(): boolean {
        return Array.from(this._classificationValid.values()).every((isValid) => isValid);
    }

    private validationComplete(values: ReadonlyMap<string, ValidationValue>): boolean {
        return Array.from(values.values()).every((value) => value.touched);
    }

    public setSupplierArticleNumberValidation(itemId: string, validationState: boolean): void {
        if (!this._supplierArticleNumberValid.has(itemId)) {
            this._supplierArticleNumberValid.set(itemId, new ValidationValue(validationState));
        } else {
            this._supplierArticleNumberValid.get(itemId)!.value = validationState;
        }
    }

    public setDeliveryDateValidation(itemId: string, validationState: boolean): void {
        if (!this._deliveryDateValid.has(itemId)) {
            this._deliveryDateValid.set(itemId, new ValidationValue(validationState));
        } else {
            this._deliveryDateValid.get(itemId)!.value = validationState;
        }
    }

    public setArticlePriceValidForOrder(itemId: string, validationState: boolean): void {
        if (!this._articlePriceValidForOrder.has(itemId)) {
            this._articlePriceValidForOrder.set(itemId, new ValidationValue(validationState));
        } else {
            this._articlePriceValidForOrder.get(itemId)!.value = validationState;
        }
    }

    public areSupplierArticleNumbersValid(): boolean {
        return Array.from(this._supplierArticleNumberValid.values()).every((isValid) => isValid);
    }

    private getSupplierArticleNumberValidationState(product: DemandProduct): boolean {
        const isError: boolean = !product.data.supplierArticleNumber && this.options.supplierArticleNumberRequired;
        return !isError;
    }

    private getArticlePriceValidForOrder(product: DemandProduct, resultsIn: PurchaseRequisitionResult): boolean {
        const isError: boolean = resultsIn === PurchaseRequisitionResult.ORDER && product.price.linePrice === 0;

        return !isError;
    }

    private getSupplierValidationState(): boolean {
        return (
            (!this.options.suppliersRequired || !!this._demand.suppliers.length) &&
            this._demand.suppliers.every((s) => s.valid)
        );
    }

    private getValidUntilValidationState(): boolean {
        if (this._demand.isAnnounced) {
            return !this.options.validUntilRequired || (!!this._demand.validUntil && this._demand.validUntil.isValid());
        }
        return true;
    }

    /**
     * EPROC-10809: Der Anfrageversand verlangt immer ein Rueckmeldedatum ("Gueltig bis") in der Zukunft -
     * es wird als Angebotsfrist an die Anfrage durchgereicht. Bewusst unabhaengig von
     * options.validUntilRequired: dieses Flag setzen nur die Workflow-Genehmigungsformulare, und
     * getValidUntilValidationState() greift erst nach dem Melden (isAnnounced) - beim Anfrageversand ist
     * die Bedarfsmeldung noch nicht announced.
     */
    private get validUntilValidForRfq(): boolean {
        return this._demand.validUntilDateValid;
    }

    private getCurrencyValidationState(): boolean {
        // One document, one currency — the pre-EPROC-11425 rule. Whether the suppliers accept that
        // currency is deliberately NOT decided here: the server reports it via the `accepted` flag of
        // `GET /demand/{id}/selectable-currencies`, and it only blocks the order conversion, not
        // announcing or handing on the demand.
        const usedCurrencies = new Set(this._demand.products.map((p) => p.price.currency));

        return usedCurrencies.size <= 1;
    }
}

export interface DemandValidatorOptions {
    supplierArticleNumberRequired: boolean;
    suppliersRequired: boolean;
    validUntilRequired: boolean;
    deliveryDateRequired: boolean;
    purchasingGroupRequired: boolean;
}
